import ShopItemSuccess from "../ShopItemSuccess";
import { useTranslation } from "react-i18next";
import {
  IShopItem,
  IUserBalance,
  ShopItemStatus,
  ShopItemStatusEnum,
} from "@/modules/shopModule/types";
import Button from "@/components/button/button";

import styles from "../ShopItemModal.module.scss";
import {
  formatBalance,
  formatPrice,
  getDiscountedPrice,
  isActiveDiscount,
} from "@/modules/shopModule/helpers";
import { stockTitleHelper } from "@/modules/shopModule/utils";

interface ShopItemModalContentProps {
  status: ShopItemStatus;
  item: IShopItem | null;
  userBalanceData: IUserBalance[];
  handleBuy: () => void;
  isBtnDisabled: boolean;
  isPurchasing: boolean;
  handleClosePopup: () => void;
}

const ShopItemModalContent: React.FC<ShopItemModalContentProps> = ({
  status,
  item,
  userBalanceData,
  handleBuy,
  isBtnDisabled,
  isPurchasing,
  handleClosePopup,
}) => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const itemName = item?.nameTranslations[lng] || item?.name;
  const categoryName = item?.category.translations[lng] || item?.category.name;
  const hasDiscount = isActiveDiscount(item?.activeDiscount);
  const shouldShowStockBadge = item?.showStock && typeof item?.stock === "number";
  const discountedPrice = getDiscountedPrice(item?.price, item?.activeDiscount);
  const currentBalance = userBalanceData.find((balance) => balance.token.id === item?.token.id);
  const newBalance =
    currentBalance && item ? Number(currentBalance.balance) - Number(discountedPrice) : null;

  if (
    status === ShopItemStatusEnum.SUCCESS ||
    status === ShopItemStatusEnum.PENDING ||
    status === ShopItemStatusEnum.ERROR
  ) {
    return <ShopItemSuccess handleClosePopup={handleClosePopup} item={item!} status={status} />;
  }

  return (
    <div className={styles.popupContent}>
      <div className={styles.itemPreview}>
        <div className={styles.gameImageContainer}>
          <img src={item?.image} alt={itemName} className={styles.popupGameImage} />

          <div className={styles.badgesOverlay}>
            {hasDiscount && (
              <div className={styles.discountBadge}>
                -{Math.floor(Number(item?.activeDiscount?.value))}%
              </div>
            )}

            {shouldShowStockBadge && (
              <div className={styles.stockBadge}>{stockTitleHelper(item.stock, t)}</div>
            )}
          </div>
        </div>

        <div className={styles.popupGameInfo}>
          <p className={styles.popupGameProvider}>{categoryName}</p>
          <h2 className={styles.popupGameTitle}>{itemName}</h2>
          {item?.showStock && typeof item?.stock === "number" && (
            <p className={styles.popupGameStock}>{t("shop.stockLeft", { stock: item?.stock })}</p>
          )}
        </div>
      </div>

      <div className={styles.summary}>
        <h3 className={styles.summaryTitle}>{t("shop.summary", "Summary")}</h3>

        <div className={styles.summaryRows}>
          <div className={styles.summaryRow}>
            <span>{t("shop.price", "Price")}</span>
            <span className={styles.summaryValue}>
              <img src={item?.token.image} alt={item?.token.name} className={styles.tokenImg} />
              <span>{formatPrice(discountedPrice)}</span>
              {hasDiscount && (
                <span className={styles.summaryOldPrice}>{formatPrice(item?.price)}</span>
              )}
            </span>
          </div>

          {newBalance !== null && (
            <div className={styles.summaryRow}>
              <span>{t("shop.newBalance", "New Balance")}</span>
              <span className={styles.newBalance}>
                <img src={item?.token.image} alt={item?.token.name} className={styles.tokenImg} />
                {formatBalance(Math.max(newBalance, 0))}
              </span>
            </div>
          )}
        </div>

      </div>

      <Button
        className={`${styles.buyButton} ${isBtnDisabled ? styles.buyButtonDisabled : ""}`}
        label={
          <div className={styles.buyButtonContent}>
            {isPurchasing ? (
              <>
                <span className={styles.buttonSpinner} aria-hidden="true" />
                <span>{t("shop.purchasing", "Purchasing...")}</span>
              </>
            ) : (
              <>
                <span>{t("shop.redeemNow", "Redeem Now")}</span>
                <span className={styles.currentPrice}>
                  <img src={item?.token.image} alt={item?.token.name} className={styles.tokenImg} />
                  <span>{formatPrice(discountedPrice)}</span>
                </span>
              </>
            )}
          </div>
        }
        disabled={isBtnDisabled}
        type={isBtnDisabled ? "secondary" : "success"}
        onClick={handleBuy}
      />
    </div>
  );
};

export default ShopItemModalContent;
